import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Heart, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public, view-only render of a caregiver-shared update (7-day link). */
export default function SharedUpdateView() {
    const { token } = useParams();
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API}/shared-update/public/${token}`)
            .then(r => setDoc(r.data))
            .catch(e => setError(e?.response?.status === 410
                ? "This link has expired — ask the family for a fresh one."
                : "This link doesn't exist."));
    }, [token]);

    return (
        <div className="min-h-screen bg-cream py-10 px-4">
            <div className="max-w-2xl mx-auto">
                <p className="text-sm font-heading font-semibold text-teal-dark flex items-center gap-1.5">
                    <Heart size={15} fill="currentColor" /> Memory Lane
                </p>
                {error ? (
                    <div className="mt-6 ml-card p-8 bg-white text-center" data-testid="shared-update-error">
                        <p className="text-sm text-[#4C5551]">{error}</p>
                    </div>
                ) : !doc ? (
                    <p className="mt-6 text-sm text-[#6E7A74]">Loading…</p>
                ) : (
                    <div className="mt-4 space-y-4" data-testid="shared-update-view" data-clarity-mask="true">
                        <div className="ml-card p-6 bg-white">
                            <h1 className="text-xl font-heading font-semibold text-[#1F2624]">
                                Care update — {doc.patient_name}
                            </h1>
                            <p className="text-xs text-[#6E7A74] mt-1">
                                Shared by {doc.caregiver_name} on {new Date(doc.created_at).toLocaleDateString()} ·
                                view-only · caregiver-reported
                            </p>
                            {doc.note && <p className="mt-3 text-sm text-[#1F2624] italic">“{doc.note}”</p>}
                        </div>
                        <SnapshotSections snapshot={doc.snapshot} />
                        <p className="text-[11px] text-[#6E7A74] flex items-center gap-1.5 pb-6">
                            <Lock size={11} /> This link expires {doc.expires_at ? new Date(doc.expires_at).toLocaleDateString() : "soon"}.
                            The family controls all sharing from their Memory Lane account.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export function SnapshotSections({ snapshot = {} }) {
    const s = snapshot || {};
    return (
        <div className="space-y-4">
            {s.behaviors && (
                <div className="ml-card p-5 bg-white" data-testid="snap-behaviors">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74]">Behaviors — last {s.behaviors.window_days} days ({s.behaviors.total})</p>
                    <div className="mt-1.5 flex gap-1.5 flex-wrap">
                        {Object.entries(s.behaviors.by_type || {}).map(([t, n]) => (
                            <span key={t} className="px-2.5 py-1 rounded-2xl bg-cream-dark/60 text-xs text-[#4C5551]">{t} × {n}</span>
                        ))}
                    </div>
                    <ul className="mt-2 space-y-1">
                        {(s.behaviors.recent || []).map((b, i) => (
                            <li key={i} className="text-xs text-[#4C5551]">
                                <span className="text-[#6E7A74]">{b.date}</span> · <strong className="text-[#1F2624]">{b.type}</strong>{b.notes ? ` — ${b.notes}` : ""}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {s.medications && (
                <div className="ml-card p-5 bg-white" data-testid="snap-medications">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74]">Medications</p>
                    <ul className="mt-1.5 space-y-0.5">
                        {(s.medications.list || []).map((m, i) => (
                            <li key={i} className="text-xs text-[#4C5551]">
                                <strong className="text-[#1F2624]">{m.name}</strong>{m.dose ? ` ${m.dose}` : ""}{m.frequency ? ` · ${m.frequency}` : ""}{m.status === "stopped" ? " · STOPPED" : ""}
                            </li>
                        ))}
                    </ul>
                    {s.medications.allergies?.medications && (
                        <p className="mt-1.5 text-xs text-coral-dark">Allergies: {s.medications.allergies.medications}</p>
                    )}
                </div>
            )}
            {s.questions && s.questions.length > 0 && (
                <div className="ml-card p-5 bg-white" data-testid="snap-questions">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74]">Questions from the family</p>
                    <ul className="mt-1.5 space-y-1">
                        {s.questions.map((q, i) => <li key={i} className="text-xs text-[#4C5551]">• {q}</li>)}
                    </ul>
                </div>
            )}
            {s.timeline && (
                <div className="ml-card p-5 bg-white" data-testid="snap-timeline">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74]">Recent timeline</p>
                    <ul className="mt-1.5 space-y-1 max-h-56 overflow-y-auto">
                        {s.timeline.map((e, i) => (
                            <li key={i} className="text-xs text-[#4C5551]">
                                <span className="text-[#6E7A74]">{e.date}</span> · <strong className="text-[#1F2624]">{e.what}</strong>{e.detail ? ` — ${e.detail}` : ""}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {s.wellness && (
                <div className="ml-card p-5 bg-white" data-testid="snap-wellness">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74]">Caregiver wellness trend ({s.wellness.check_ins} check-ins)</p>
                    <div className="mt-1.5 flex gap-1.5 flex-wrap">
                        {Object.entries(s.wellness.domain_averages || {}).map(([d, v]) => (
                            <span key={d} className="px-2.5 py-1 rounded-2xl bg-cream-dark/60 text-xs text-[#4C5551]">{d}: {v}/10</span>
                        ))}
                    </div>
                    <p className="text-[11px] text-[#6E7A74] mt-1">{s.wellness.note}</p>
                </div>
            )}
        </div>
    );
}
